import { InboundIcon } from "@minket/brand/icons/inbound";
import { RefreshIcon } from "@minket/brand/icons/refresh";
import { Button } from "@minket/ui/components/button";
import { CopyButton } from "@minket/ui/components/copy-button";
import { fieldLine } from "@minket/ui/lib/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { type ReceivingDomain, recordStatus } from "@/components/inbound/model";
import { useMarkStepDone } from "@/components/onboarding/mark-done";
import { StepPage } from "@/components/onboarding/step-page";
import { UnavailableAction } from "@/components/page/action";
import { DataList } from "@/components/page/data-list";
import { Field } from "@/components/page/field";
import { Seams } from "@/components/page/seams";
import { Notice } from "@/components/page/states";
import { describeError } from "@/lib/api";
import { endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiKey, apiQuery, apiRequest } from "@/lib/query";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/onboarding/inbound")({
  component: InboundStep,
});

const turnOn = { label: "Turn on receiving", icon: InboundIcon };

function InboundStep() {
  return (
    <StepPage
      path="/onboarding/inbound"
      intro="Turn receiving on for one of your domains and add the MX record that brings its mail to SES."
      asks={[
        {
          title: "A domain",
          text: "Receiving takes mail to every address at it, support@ as much as billing@.",
        },
        {
          title: "An MX record",
          text: "Shown to copy here, or written on a Cloudflare zone from the domain's page.",
        },
      ]}
      action={turnOn}
      control={<StepControl />}
    />
  );
}

/** Turn receiving on for a domain, show its MX record, and check it until it is found. */
function StepControl() {
  const { project } = useWorkspace();
  const projectId = project?.id ?? "";
  const canManage = useCan("inbound.manage");
  const queryClient = useQueryClient();
  const list = useQuery({ ...apiQuery(endpoints.receiving, { projectId }), enabled: !!project });
  const [chosen, setChosen] = useState("");
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.receiving, { projectId }) });
  const enable = useMutation({
    mutationFn: (domainId: string) =>
      apiRequest(endpoints.updateDomain, { projectId, domainId }, { body: { receiving: true } }),
    onSuccess: refresh,
  });
  const check = useMutation({
    mutationFn: (domainId: string) => apiRequest(endpoints.checkReceiving, { projectId, domainId }),
    onSuccess: refresh,
  });

  const unavailable = (reason: string) => (
    <UnavailableAction {...turnOn} reason={reason} align="start" />
  );
  if (!project) return unavailable("Choose a project first");
  if (list.isPending) return unavailable("Loading domains");
  if (list.isError) return unavailable(describeError(list.error).message);
  if (list.data.data.length === 0) return unavailable("Add a domain first");
  if (!canManage) return unavailable("Viewers cannot turn receiving on");
  const receiving = list.data.data.find((d) => d.receiving);
  const error = enable.error ?? check.error;

  if (!receiving) {
    const domainId = chosen || list.data.data[0]?.domain_id || "";
    return (
      <div className="grid max-w-md min-w-0 gap-3">
        <Field label="Domain">
          {(control) => (
            <select
              {...control}
              className={`${fieldLine} type-data`}
              value={domainId}
              onChange={(e) => setChosen(e.target.value)}
            >
              {list.data.data.map((d) => (
                <option key={d.domain_id} value={d.domain_id}>
                  {d.name}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Button
          className="w-fit"
          loading={enable.isPending}
          onClick={() => enable.mutate(domainId)}
        >
          <InboundIcon className="size-5" />
          Turn on receiving
        </Button>
        {error ? <Notice tone="destructive">{describeError(error).message}</Notice> : null}
      </div>
    );
  }
  return (
    <ReceivingState
      domain={receiving}
      checking={check.isPending}
      error={error}
      onCheck={() => check.mutate(receiving.domain_id)}
    />
  );
}

function Copyable({ value, label }: { value: string; label: string }) {
  return (
    <span className="inline-flex max-w-full items-start">
      <span className="type-data min-w-0 wrap-break-word">
        <Seams>{value}</Seams>
      </span>
      <CopyButton value={value} label={label} className="-my-0.5" />
    </span>
  );
}

function ReceivingState({
  domain,
  checking,
  error,
  onCheck,
}: {
  domain: ReceivingDomain;
  checking: boolean;
  error: unknown;
  onCheck: () => void;
}) {
  const mx = domain.mx;
  const found = mx?.status === "verified" && domain.rule.status === "active";
  const markDone = useMarkStepDone("inbound");
  useEffect(() => {
    if (found) markDone();
  }, [found, markDone]);
  const status = mx ? recordStatus[mx.status] : null;

  return (
    <div className="grid max-w-2xl min-w-0 gap-4">
      <p className="text-sm">
        Mail to any address at <span className="type-data">{domain.name}</span> comes to this
        project once this record is in its DNS.
      </p>
      {mx ? (
        <DataList
          items={[
            { label: "Type", value: <span className="type-data">MX</span>, nowrap: true },
            {
              label: "Name",
              value: <Copyable value={mx.name} label="Copy the MX record's name" />,
            },
            {
              label: "Value",
              value: <Copyable value={mx.value} label="Copy the MX record's value" />,
            },
            {
              label: "Priority",
              value: <span className="type-data">{mx.priority ?? 10}</span>,
              nowrap: true,
            },
          ]}
        />
      ) : null}
      <div role="status">
        {found ? (
          <Notice tone="success">
            The MX record is in place and SES stores this domain's mail.
          </Notice>
        ) : (
          <Notice tone={status?.tone === "primary" ? "neutral" : (status?.tone ?? "neutral")}>
            {status?.label ?? "Not checked"}. {mx?.error ?? "Check again once you have added it."}
          </Notice>
        )}
      </div>
      {!found ? (
        <Button variant="outline" className="w-fit" loading={checking} onClick={onCheck}>
          <RefreshIcon className="size-5" />
          Check MX record
        </Button>
      ) : null}
      {error ? <Notice tone="destructive">{describeError(error).message}</Notice> : null}
    </div>
  );
}
